import { useCallback, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useDeepCompareEffect } from "@fuse/hooks";
import _ from "@lodash";
import { isNull } from "lodash";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { getSelectedRecords } from "app/main/apps/common/AuraFunctions";
import { showMessage } from "app/store/fuse/messageSlice";
import { performAction } from "app/main/apps/e-commerce/store/orderSlice";
import AuraBaseDialog from "../../../common/AuraBaseDialog";
import {
  closeContainerDialog,
  updateContainerOrderItem,
} from "../../store/shipmentOrderSlice";
import ContainerFormContent from "./ContainerFormContent";
import ContainerHeader from "./ContainerHeader";
import useSubmitOnce from "../useSubmitOnce";

const schema = yup.object().shape({
  order_detail: yup.array().of(
    yup.object().shape({
      quantity: yup
        .number()
        .nullable()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .required("Required")
        .test({
          message: "Must be greater than 0",
          test: (val, { parent }) => {
            if (parent.is_selected && parent.original_qty > 0 && val === 0) {
              return false;
            }
            return true;
          },
        })
        .test({
          message: "Can not be greater than actual quantity",
          test: (val, { parent, createError, path }) => {
            if (parent.is_selected && (val > parent.original_qty))
              return createError({path, message: `Max quantity ${parent.original_qty}`,
            })
            return true;
          },
        }),
      box_number_from: yup
        .number()
        .nullable()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test({
          message: "Must be greater than 0",
          test: (val, { parent }) => {
            if (parent.original_qty > 0 && val === 0) {
              return false;
            }
            return true;
          },
        })
        .test({
          message: "Required",
          test: (val, { parent }) => {
            if (parent.is_selected && parent.original_qty > 0 && (Number.isNaN(val) || val === "" || isNull(val))) {
              return false;
            }
            return true;
          },
        }),
      box_number_to: yup
        .number()
        .nullable()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test({
          message: "Must be greater than 0",
          test: (val, { parent }) => {
            if (parent.original_qty > 0 && val === 0) {
              return false;
            }
            return true;
          },
        })
        .test({
          message: "Required",
          test: (val, { parent }) => {
            if (parent.is_selected && parent.original_qty > 0 && (Number.isNaN(val) || val === "" || isNull(val))) {
              return false;
            }
            return true;
          },
        })
        .test({
          message: "Enter Greater Box No",
          test: (val, { parent }) => {
            if (parent.original_qty > 0 && val < parent.box_number_from) return false;
            return true;
          },
        })
        .test({
          message: "Box No exceeded",
          test: (val, { parent }) => {
            const quantity = parent.quantity || 0;
            const from = parent.box_number_from || 0;
            const to = parent.box_number_to || 0;
            const diff = (to - from) + 1;
            if (parent.original_qty > 0 && quantity && diff > quantity) return false;
            return true;
          },
        }),
    })
  ),
});

function getInitValues() {
  return {
    order_detail: [],
  };
}

function ContainerDialog(props) {
  const dispatch = useDispatch();
  const containerDialog = useSelector(({ backofficeApp }) => {
    return backofficeApp?.shipmentOrder?.containerDialog;
  });
  const [orderWeight, setOrderWeight] = useState(0);
  const [startBoxNumber, setStartBoxNumber] = useState(1);
  const dialogParam = containerDialog?.param;
  const methods = useForm({
    mode: "onChange",
    defaultValues: getInitValues(),
    resolver: yupResolver(schema),
  });
  const { reset, formState, control, getValues, watch, setValue, clearErrors, trigger } = methods;
  const { isValid } = formState;
  // PGSS-4966: the dialog stays open until the load request returns, so every extra click on Update
  // sent another load and the backend made another split order. One load at a time.
  const [loadingOrders, runLoadOnce] = useSubmitOnce();
  const { fields, replace } = useFieldArray({
    name: "order_detail",
    control,
  });
  const watchFieldArray = watch("order_detail");
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index]
    };
  });
  useDeepCompareEffect(() => {
    if(controlledFields && controlledFields.length > 0) {
      trigger();
      const orderDetails = getSelectedRecords(controlledFields);
      const weight = _.sumBy(orderDetails, (detail) => {
        return _.multiply(_.toNumber(detail?.quantity || 0), _.toNumber(detail.weight))
      });
      setOrderWeight(weight);
    }
  }, [controlledFields, setValue]);

  const isAddToContainerBoxNumber =
    containerDialog?.param?.startOfNewNBoxNumber; // this field is the box number from add to container page
  const isAddTocontainer = containerDialog?.param?.isAddTocontainer; // represents the page from add to container
  const isLoadTypeContainer = containerDialog?.param?.parent === "load" && containerDialog?.param?.startOfNewNBoxNumber > 0;
  /**
   * Initialize Dialog with Data
   */
  const initDialog = useCallback(() => {
    if (dialogParam) {
      if (containerDialog?.data) {
        const order = containerDialog?.data;
        let startFromNumber = null;
        order?.order_detail.forEach((detail) => {
          // Added detail.original_qty condition to avoid setting null values in the box number
          if(detail.is_selected === 1 && detail.original_qty > 0 && !startFromNumber ){
            startFromNumber = detail.box_number_from;
            if(isLoadTypeContainer){
              startFromNumber = isAddToContainerBoxNumber;
            }
            setStartBoxNumber(startFromNumber);
          }
        });
        //  this will execute to set the box number from add to container number
        if (isAddToContainerBoxNumber && isAddTocontainer) {
          startFromNumber = isAddToContainerBoxNumber;
          setStartBoxNumber(isAddToContainerBoxNumber);
        }
        if(!startFromNumber) {
          // Comes here if selected record(s) with actual quantity is 0. So we have to reset the box number
          setStartBoxNumber(1);
        }
        reset({ ...containerDialog?.data });
      }
      setOrderWeight(dialogParam.orderWeight);
    }
  }, [dialogParam]);

  /**
   * On Dialog Open
   */
  useEffect(() => {
    if (containerDialog?.props.open) {
      initDialog();
    }
  }, [containerDialog?.props.open, initDialog]);

  /**
   * Close Dialog
   */
  function closeDialog(event, reason) {
    // Added condition to not close dialog on back drop click
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      reset(getInitValues());
      dispatch(closeContainerDialog());
    }
  }

  /**
   * Form Submit
   */
  function onSubmit(data) {
    // this will trigger the perform action when orders are added in the container from container page
    if (containerDialog?.param?.parent === "container-list") {
      runLoadOnce(() => addToContainerOrders(data));
    } else {
      closeDialog();
      const dataParam = {
        customerId: dialogParam.customerId,
        orderId: data.id,
        orderItem: data,
        // PGSS-5416 / PGSS-5584: leave the following orders' box numbers alone when staff or a
        // supplier edit an existing container.
        preserveOtherOrderBoxNumbers: Boolean(dialogParam?.preserveOtherOrderBoxNumbers),
      };
      dispatch(updateContainerOrderItem(dataParam));
    }
  }

  // this method is used to generate payload for adding orders in the container
  function addToContainerOrders(data) {
    let inventory = [];
    let index = 0;
    const shipmentId = containerDialog?.param?.containerId;
    if (shipmentId) {
      const result = loadOrdersPayload(data, inventory, index);
      inventory = result.inventory;
      index = result.index;

      const actionType = "load";
      const payload = {
        ui_id: "EDIT_SHIPMENT",
        data: {
          shipment_id: Number(shipmentId),
          inventory,
          actionType,
        },
      };
      
      return dispatch(performAction(payload)).then((res) => {
        if (!res.error) {
          // PGSS-5125: surface the "already loaded in another container" block message instead of a plain
          // "Success" when the backend skipped an order that is already in another active container.
          if (res.payload?.loadBlocked) {
            dispatch(
              showMessage({
                type: "warning",
                message:
                  res.payload.message ||
                  "Some orders are already loaded in another container and were not added. Unload them there first.",
              })
            );
          } else {
            dispatch(showMessage({ type: "success", message: "Success" }));
          }
        }else{
          dispatch(showMessage({ type: "error", message: "Fail" }));
        }
        closeDialog();
        if (containerDialog?.param?.onContainerListDialogClose) {
          containerDialog.param.onContainerListDialogClose();
        }
      });
    }
    return null;
  }

  // payload during load of orders
  const loadOrdersPayload = (o, inventory, currentIndex) => {
    const newInventory = [...inventory];
    newInventory.push({ order_id: o.id });
    const boxNumber = [];
    o?.order_detail.forEach((od) => {
      boxNumber.push({
        order_detail_id: od.id,
        box_number:
          od.original_qty > 0
            ? `${od.box_number_from}-${od.box_number_to}`
            : "0-0",
        quantity: od.newQuantity || od.quantity,
      });
    });

    newInventory[currentIndex].box_number = boxNumber;
    return {
      inventory: newInventory,
      index: currentIndex + 1,
    };
  };

  function disableSubmitButton() {
    return loadingOrders || !isValid;
  }

  const headerData = {
    enable: true,
    component: ContainerHeader,
    data: { data: containerDialog?.data, param: dialogParam, orderWeight},
  };

  return containerDialog &&
    containerDialog.props &&
    containerDialog.props.open ? (
    <>
      <FormProvider {...methods}>
        <AuraBaseDialog
          dialogProps={containerDialog.props}
          closeDialog={closeDialog}
          dialogParam={dialogParam}
          onSubmit={onSubmit}
          disableSubmitButton={disableSubmitButton}
          header={headerData}
        >
          <ContainerFormContent
            fields={fields}
            replace={replace}
            startBoxNumber={startBoxNumber}
            isNewShipment={dialogParam.isNewShipment}
            showSelectionBox={dialogParam.showSelectionBox}
            isAddTocontainer={isAddTocontainer} // this is an indicator to add order to container with the box number from previous shipments 
            isLoadTypeContainer={isLoadTypeContainer}
         />
        </AuraBaseDialog>
      </FormProvider>
    </>
  ) : (
    <></>
  );
}

export default ContainerDialog;
